import { useState } from "react";
import CONFIG from "../config";

// ── THE TWO-WEEK FIX ──────────────────────────────────────────────
// The old line was:
//   const API = "https://trm.fxcapitalindia.in/api/bills/rates";
// but the server's real path has /api twice: the site folder is /api
// AND the controller route starts with api/. All working pages
// (Dashboard, Orders) build their address from CONFIG, which adds
// the first /api — so we do the same here.
const API = `${CONFIG.API}/api/bills/rates`;
const PAIRS = ["USDINR","EURINR","GBPINR","AEDINR","SGDINR","AUDINR"];

export default function RateUpload({ token }) {
  const headers = { "Content-Type": "application/json", authorization: `Bearer ${token}` };

  const [tab,     setTab]     = useState("manual");
  const [msg,     setMsg]     = useState("");
  const [msgType, setMsgType] = useState("ok");

  const showMsg = (text, type = "ok") => {
    setMsg(text); setMsgType(type);
    setTimeout(() => setMsg(""), 4000);
  };

  // ── Manual ───────────────────────────────────────────────────
  const [pair,  setPair]  = useState("USDINR");
  const [date,  setDate]  = useState("");
  const [open,  setOpen]  = useState("");
  const [high,  setHigh]  = useState("");
  const [low,   setLow]   = useState("");
  const [close, setClose] = useState("");

  const saveManual = async () => {
    if (!date || !open || !high || !low || !close)
      return showMsg("All fields required", "err");

    const body = JSON.stringify({
      currencyPair: pair,
      rateDate:     date,
      open:  parseFloat(open),
      high:  parseFloat(high),
      low:   parseFloat(low),
      close: parseFloat(close)
    });

    try {
      // Try POST first
      const res  = await fetch(`${API}/manual`, { method: "POST", headers, body });
      const data = await res.json();
      if (!res.ok) return showMsg(data?.message || data?.title || "Error", "err");

      // If already exists — automatically update via PUT
      if (data.skipped) {
        const putRes  = await fetch(`${API}/manual`, { method: "PUT", headers, body });
        const putData = await putRes.json();
        if (!putRes.ok) return showMsg(putData?.message || putData?.title || "Update failed", "err");
        showMsg(`✅ Updated — ${pair} on ${date}`);
      } else {
        showMsg(`✅ Saved — ${pair} on ${date}`);
      }

      setDate(""); setOpen(""); setHigh(""); setLow(""); setClose("");
    } catch { showMsg("Error saving", "err"); }
  };

  // ── CSV Upload ────────────────────────────────────────────────
  const [uploadPair,   setUploadPair]   = useState("USDINR");
  const [csvFile,      setCsvFile]      = useState(null);
  const [uploading,    setUploading]    = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const uploadCsv = async () => {
    if (!csvFile) return showMsg("Select a file first", "err");
    setUploading(true);
    setUploadResult(null);
    try {
      const text = await csvFile.text();
      const res  = await fetch(`${API}/upload`, {
        method: "POST", headers,
        body: JSON.stringify({ currencyPair: uploadPair, csvData: text })
      });
      const data = await res.json();
      if (!res.ok) return showMsg(data?.message || data?.title || "Upload failed", "err");
      setUploadResult(data);
      showMsg(data.message, data.errors > 0 ? "warn" : "ok");
    } catch { showMsg("Upload error", "err"); }
    setUploading(false);
  };

  // ── View ─────────────────────────────────────────────────────
  const [viewPair,  setViewPair]  = useState("USDINR");
  const [rates,     setRates]     = useState([]);
  const [loading,   setLoading]   = useState(false);

  const loadRates = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/view?currencyPair=${viewPair}&limit=60`, { headers });
      const data = await res.json();
      setRates(Array.isArray(data) ? data : []);
    } catch { showMsg("Error loading", "err"); }
    setLoading(false);
  };

  // ── Styles ───────────────────────────────────────────────────
  const inp = {
    width: "100%", padding: "7px 10px", borderRadius: "6px",
    border: "1px solid #CBD5E1", fontSize: "13px",
    boxSizing: "border-box"
  };
  const lbl = { fontSize: "12px", color: "#555", marginBottom: "3px", display: "block" };
  const btn = (bg) => ({
    padding: "8px 18px", borderRadius: "6px", border: "none",
    background: bg, color: "#fff", cursor: "pointer", fontSize: "13px"
  });

  return (
    <div style={{ padding: "16px", fontFamily: "Arial, sans-serif",
      fontSize: "13px", maxWidth: "860px" }}>

      {/* Title */}
      <div style={{ fontSize: "15px", fontWeight: "600",
        color: "#1F4E79", marginBottom: "16px" }}>
        📈 FX Rate Management — Admin Only
      </div>

      {/* Message */}
      {msg && (
        <div style={{
          padding: "8px 14px", borderRadius: "6px", marginBottom: "14px",
          background: msgType === "err"  ? "#fce4d6" :
                      msgType === "warn" ? "#fff2cc" : "#e2efda",
          color:      msgType === "err"  ? "#c00"    :
                      msgType === "warn" ? "#7d6608" : "#375623",
          border: `1px solid ${msgType === "err" ? "#c00" : msgType === "warn" ? "#f0a500" : "#70ad47"}`
        }}>{msg}</div>
      )}

      {/* Tab switcher */}
      <div style={{ display: "flex", borderBottom: "2px solid #e2e8f0",
        marginBottom: "16px" }}>
        {[
          { key: "manual", label: "✏️ Manual Entry" },
          { key: "upload", label: "📂 CSV Upload"   },
          { key: "view",   label: "📊 View Rates"   },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: "8px 20px", border: "none", cursor: "pointer",
            borderRadius: "6px 6px 0 0", marginRight: "4px",
            fontSize: "13px", fontWeight: "600",
            background: tab === t.key ? "#1F4E79" : "#f1f5f9",
            color:      tab === t.key ? "#fff"    : "#64748b"
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── Manual Entry ──────────────────────────────────────── */}
      {tab === "manual" && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0",
          borderRadius: "8px", padding: "16px" }}>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
            gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={lbl}>Currency Pair</label>
              <select value={pair} onChange={e => setPair(e.target.value)} style={inp}>
                {PAIRS.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Date</label>
              <input type="date" value={date}
                onChange={e => setDate(e.target.value)} style={inp} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr",
            gap: "12px", marginBottom: "16px" }}>
            <div>
              <label style={lbl}>Open</label>
              <input type="number" step="0.0001" value={open}
                onChange={e => setOpen(e.target.value)}
                style={inp} placeholder="0.0000" />
            </div>
            <div>
              <label style={lbl}>High</label>
              <input type="number" step="0.0001" value={high}
                onChange={e => setHigh(e.target.value)}
                style={inp} placeholder="0.0000" />
            </div>
            <div>
              <label style={lbl}>Low</label>
              <input type="number" step="0.0001" value={low}
                onChange={e => setLow(e.target.value)}
                style={inp} placeholder="0.0000" />
            </div>
            <div>
              <label style={lbl}>Close</label>
              <input type="number" step="0.0001" value={close}
                onChange={e => setClose(e.target.value)}
                style={inp} placeholder="0.0000" />
            </div>
          </div>

          <button onClick={saveManual} style={btn("#1F4E79")}>
            💾 Save Rate
          </button>
        </div>
      )}

      {/* ── CSV Upload ─────────────────────────────────────────── */}
      {tab === "upload" && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0",
          borderRadius: "8px", padding: "16px" }}>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr",
            gap: "12px", marginBottom: "16px" }}>
            <div>
              <label style={lbl}>Currency Pair</label>
              <select value={uploadPair}
                onChange={e => setUploadPair(e.target.value)} style={inp}>
                {PAIRS.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>CSV File</label>
              <input type="file" accept=".csv"
                onChange={e => { setCsvFile(e.target.files[0] || null); setUploadResult(null); }}
                style={{ ...inp, padding: "5px" }} />
            </div>
          </div>

          <button onClick={uploadCsv} disabled={uploading || !csvFile}
            style={{ ...btn("#1F4E79"), opacity: uploading || !csvFile ? 0.6 : 1 }}>
            {uploading ? "Uploading..." : "📤 Upload"}
          </button>

          {uploadResult && (
            <div style={{ marginTop: "16px", display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>
              {[
                { label: "Inserted", value: uploadResult.inserted, bg: "#e2efda", color: "#375623" },
                { label: "Updated",  value: uploadResult.updated,  bg: "#fff2cc", color: "#7d6608" },
                { label: "Errors",   value: uploadResult.errors,   bg: "#fce4d6", color: "#c00"    },
              ].map(c => (
                <div key={c.label} style={{ background: c.bg, borderRadius: "8px",
                  padding: "12px", textAlign: "center" }}>
                  <div style={{ fontSize: "11px", color: c.color }}>{c.label}</div>
                  <div style={{ fontSize: "26px", fontWeight: "700",
                    color: c.color }}>{c.value}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: "16px", background: "#f8fafc",
            border: "1px solid #e2e8f0", borderRadius: "6px", padding: "12px" }}>
            <div style={{ fontSize: "11px", fontWeight: "600",
              color: "#555", marginBottom: "6px" }}>Expected CSV Format</div>
            <div style={{ fontFamily: "monospace", fontSize: "11px",
              color: "#374151", lineHeight: "1.8" }}>
              DATE,OPEN,HIGH,LOW,CLOSE<br/>
              8 Jun2026,84.95,85.56,84.94,85.25
            </div>
            <div style={{ fontSize: "11px", color: "#888", marginTop: "6px" }}>
              ✅ Duplicate dates are updated with the new values automatically
            </div>
          </div>
        </div>
      )}

      {/* ── View Rates ─────────────────────────────────────────── */}
      {tab === "view" && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0",
          borderRadius: "8px", padding: "16px" }}>

          <div style={{ display: "flex", gap: "10px",
            alignItems: "flex-end", marginBottom: "14px" }}>
            <div>
              <label style={lbl}>Currency Pair</label>
              <select value={viewPair}
                onChange={e => setViewPair(e.target.value)}
                style={{ ...inp, width: "140px" }}>
                {PAIRS.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <button onClick={loadRates} style={btn("#1F4E79")}>
              🔍 Load Rates
            </button>
            {rates.length > 0 && (
              <span style={{ fontSize: "12px", color: "#64748b", alignSelf: "center" }}>
                {rates.length} records
              </span>
            )}
          </div>

          <div style={{ overflowX: "auto", border: "1px solid #e2e8f0",
            borderRadius: "6px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
              <thead>
                <tr style={{ background: "#1F4E79", color: "#fff" }}>
                  {["Date","Pair","Open","High","Low","Close"].map(h => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left",
                      fontWeight: "500", fontSize: "11px" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={{ padding: "20px",
                    textAlign: "center", color: "#888" }}>Loading...</td></tr>
                ) : rates.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: "20px",
                    textAlign: "center", color: "#888" }}>
                    Select a currency and click Load Rates
                  </td></tr>
                ) : rates.map((r, i) => (
                  <tr key={r.rateDate}
                    style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb",
                      borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "7px 12px", fontWeight: "500" }}>{r.rateDate}</td>
                    <td style={{ padding: "7px 12px" }}>{r.currencyPair}</td>
                    <td style={{ padding: "7px 12px", textAlign: "right",
                      fontFamily: "monospace" }}>{Number(r.open).toFixed(4)}</td>
                    <td style={{ padding: "7px 12px", textAlign: "right",
                      fontFamily: "monospace", color: "#375623",
                      fontWeight: "600" }}>{Number(r.high).toFixed(4)}</td>
                    <td style={{ padding: "7px 12px", textAlign: "right",
                      fontFamily: "monospace", color: "#c00",
                      fontWeight: "600" }}>{Number(r.low).toFixed(4)}</td>
                    <td style={{ padding: "7px 12px", textAlign: "right",
                      fontFamily: "monospace", fontWeight: "600",
                      color: "#1F4E79" }}>{Number(r.close).toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}